<template>
  <div class="image-details">
    <ul class="image-details__list scrollbar">
      <li v-if="options.showName" class="image-details__list-item">
        <div class="image-details__list-item-icon">
          <SVGIcon
            :iconName="'image'"
            :width="20"
            :height="20"
          />
        </div>
        <div class="image-details__list-item-details">
          <span :title="`${ imageObj.name }`">{{ imageObj.name }}</span>
          <div class="image-details__list-item-sub-details">
            <span :title="`${ displayWidth }w x ${ displayHeight }h`">{{ displayWidth }}w x {{ displayHeight }}h</span>
          </div>
        </div>
      </li>
      <li v-if="options.showDate" class="image-details__list-item">
        <div class="image-details__list-item-icon">
          <SVGIcon
            :iconName="'event'"
            :width="20"
            :height="20"
          />
        </div>
        <div class="image-details__list-item-details">
          <span :title="displayDateText">{{ displayDateText }}</span>
        </div>
      </li>
      <li v-if="options.showFileLocation" class="image-details__list-item">
        <div class="image-details__list-item-icon">
          <SVGIcon
            :iconName="'folder'"
            :width="20"
            :height="20"
          />
        </div>
        <div class="image-details__list-item-details">
          <span>File Location</span>
          <div class="image-details__list-item-sub-details _overflow-wrap">
            <span :title="imageObj.url">{{ imageObj.url }}</span>
          </div>
          <!-- <span v-if="options.showExternalURL">External URL</span>
          <div v-if="options.showExternalURL" class="image-details__list-item-sub-details _overflow-wrap">
            <span v-if="imageObj.ext_url" :title="imageObj.ext_url">{{ imageObj.ext_url }}</span>
            <span v-else><i>No External URL</i></span>
          </div> -->
        </div>
      </li>
      <li v-if="options.showImageLocation" class="image-details__list-item">
        <div class="image-details__list-item-icon">
          <SVGIcon
            :iconName="'place'"
            :width="20"
            :height="20"
          />
        </div>
        <div class="image-details__list-item-details">
          <span>Image Location</span>
          <div class="image-details__list-item-sub-details _overflow-wrap">
            <span :title="imageLocationText">{{ imageLocationText }}</span>
          </div>
        </div>
      </li>
      <li v-if="options.showCameraSource" class="image-details__list-item">
        <div class="image-details__list-item-icon">
          <SVGIcon
            :iconName="'photo_camera'"
            :width="20"
            :height="20"
          />
        </div>
        <div class="image-details__list-item-details">
          <span>Camera</span>
          <div class="image-details__list-item-sub-details _overflow-wrap">
            <span v-if="imageObj.source" :title="imageObj.source">{{ imageObj.source }}</span>
            <span v-else><i>Unknown Device</i></span>
          </div>
        </div>
      </li>
      <li v-if="groups.length > 0" class="image-details__list-item">
        <div class="image-group">
          <label class="image-group__label">Group</label>
          <ImageGroupSelector :imageGroupId="imageGroupId" :groups="groups" @change="handleImageGroupChanged" />
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import SVGIcon from '@/components/SVGIcon.vue';
import ImageGroupSelector from '@/components/ImageGroupSelector.vue';
import EncodedIdField from '@/components/EncodedIdField.vue';
import { useEditorStore } from '@/stores/useEditorStore.js';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';

// Props
const props = defineProps({
  imageObj: {
    type: Object,
    default: () => ({}),
  },
  options: {
    type: Object,
    default: () => ({
      showName: true,
      showDate: true,
      showFileLocation: false,
      showExternalURL: true,
      showImageLocation: true,
      showCameraSource: true,
    }),
  },
  sourceDataset: {
    type: Object,
    default: () => ({}),
  },
});

// Emits (currently empty, but declared for future extensibility)
defineEmits([]);

// Store
const editorStore = useEditorStore();
const { currentSample, imageGroupId, imageDimensions } = storeToRefs(editorStore);

// Toast
const $toast = useToast();

// Computed
const groups = computed(() => props.sourceDataset?.groups || []);

// Fallback dimensions loaded from the image itself when imageObj.width/height are 0/missing
const loadedWidth = ref(0);
const loadedHeight = ref(0);

const displayWidth = computed(() => {
  if (props.imageObj?.width) return props.imageObj.width;
  if (currentSample.value?.image?.naturalWidth) return currentSample.value.image.naturalWidth;
  if (imageDimensions.value?.width) return imageDimensions.value.width;
  return loadedWidth.value || 0;
});
const displayHeight = computed(() => {
  if (props.imageObj?.height) return props.imageObj.height;
  if (currentSample.value?.image?.naturalHeight) return currentSample.value.image.naturalHeight;
  if (imageDimensions.value?.height) return imageDimensions.value.height;
  return loadedHeight.value || 0;
});

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizePayload = (value) => {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  }
  return typeof value === 'object' ? value : null;
};

const extractCoordinates = (value) => {
  const payload = normalizePayload(value);
  if (!payload) {
    return null;
  }

  const lat = toFiniteNumber(payload?.lat ?? payload?.latitude ?? payload?.gps?.lat ?? payload?.gps?.latitude);
  const lon = toFiniteNumber(payload?.lon ?? payload?.longitude ?? payload?.gps?.lon ?? payload?.gps?.longitude);
  if (lat === null || lon === null) {
    return null;
  }

  return { lat, lon };
};

const isGpsLikeTopic = (annotation) => {
  const type = String(annotation?.type || '').toLowerCase();
  const topic = String(annotation?.topic || annotation?.topic_name || annotation?.source || '').toLowerCase();
  return type === 'gps' || type.startsWith('gps_') || topic === 'gps' || topic.startsWith('gps_');
};

const annotationCoordinates = computed(() => {
  const annotations = currentSample.value?.annotations || [];
  for (const annotation of annotations) {
    if (!isGpsLikeTopic(annotation)) {
      continue;
    }

    const fromDataJson = extractCoordinates(annotation?.data_json);
    if (fromDataJson) {
      return fromDataJson;
    }

    const fromAnnotation = extractCoordinates(annotation);
    if (fromAnnotation) {
      return fromAnnotation;
    }

    const fromRaw = extractCoordinates(annotation?.raw);
    if (fromRaw) {
      return fromRaw;
    }
  }

  return null;
});

const imageCoordinates = computed(() => {
  return extractCoordinates(props.imageObj || {});
});

const resolvedCoordinates = computed(() => {
  const imageCoords = imageCoordinates.value;
  if (imageCoords && (imageCoords.lat !== 0 || imageCoords.lon !== 0)) {
    return imageCoords;
  }
  return annotationCoordinates.value || imageCoords;
});

const imageLocationText = computed(() => {
  if (!resolvedCoordinates.value) {
    return 'No location coordinates';
  }
  return `${resolvedCoordinates.value.lat}, ${resolvedCoordinates.value.lon}`;
});

function parseDateFromSampleName(sampleName) {
  const name = String(sampleName || '').trim();
  if (!name) {
    return null;
  }

  const match = name.match(/_(\d{4})_(\d{2})_(\d{2})_(\d{2})_(\d{2})_(\d{2})(?:_\d+)?(?:\.[^.]+)+$/);
  if (!match) {
    return null;
  }

  const [, year, month, day, hours, minutes, seconds] = match;
  const parsedDate = new Date(Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
    Number(seconds),
  ));

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function resolveDisplayDate(imageObj) {
  const rawDate = String(imageObj?.date || '').trim();
  if (rawDate && !rawDate.startsWith('0001-01-01')) {
    const parsedDate = new Date(rawDate);
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate;
    }
  }

  return parseDateFromSampleName(imageObj?.name);
}

const displayDateText = computed(() => {
  const resolvedDate = resolveDisplayDate(props.imageObj);
  return resolvedDate ? resolvedDate.toUTCString() : 'Mon, 01 Jan 0001 00:00:00 GMT';
});

watch(
  () => [props.imageObj?.url, props.imageObj?.width, props.imageObj?.height],
  ([url, w, h]) => {
    loadedWidth.value = 0;
    loadedHeight.value = 0;
    if (url && (!w || !h)) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        loadedWidth.value = img.naturalWidth;
        loadedHeight.value = img.naturalHeight;
      };
      img.onerror = () => {
        // Retry without crossOrigin in case CORS headers are missing
        const fallback = new Image();
        fallback.onload = () => {
          loadedWidth.value = fallback.naturalWidth;
          loadedHeight.value = fallback.naturalHeight;
        };
        fallback.src = url;
      };
      img.src = url;
    }
  },
  { immediate: true },
);
// Methods
async function handleImageGroupChanged(group_id) {
  const oldGroupId = imageGroupId.value;
  currentSample.value.imageObj.group_id = group_id;

  const dataConnect = new DatastoreConnect();
  const data = await dataConnect.setImageGroupId({ image_id: currentSample.value.imageObj.id, group_id });

  if (data.error) {
    $toast.open({
      queue: true,
      dismissible: true,
      message: `Failed to set group: ${data.error.message}`,
      type: 'error',
      position: 'top',
    });
    // Revert group_id due to error
    currentSample.value.imageObj.group_id = oldGroupId;
  }
}
</script>

<style lang="scss" scoped>
.image-details {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 300px;
  max-width: 300px;
  padding: 0;
  z-index: 2;

  &__list {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    padding: 0;
    margin: 0;
    gap: 8px;
    overflow-y: auto;
    list-style-type: none;
  }

  &__list-item {
    display: flex;
    flex-direction: row;
    width: 100%;
    padding: 0 16px;
  }

  &__list-item-icon {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    width: 40px;
    min-width: 40px;
  }

  &__list-item-details {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    text-align: left;
    overflow: hidden;

    span {
      font-weight: 600;
      font-size: 0.9rem;
      line-height: 1.1rem;
      overflow: hidden;
      margin-bottom: 2px;
      overflow-wrap: anywhere;
    }
  }

  &__list-item-sub-details {
    display: flex;
    flex-direction: row;
    flex: 1 1 auto;
    text-align: left;

    span {
      font-weight: 500;
      font-size: 0.9rem;
      @include themify() {
        color: themed('body-text-color-secondary');
      }
    }

    &._overflow-wrap span {
      white-space: unset;
      overflow-wrap: anywhere;
    }
  }
}

.image-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;

  &__label {
    color: var(--body-text-color-secondary);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    margin-bottom: 2px;
  }
}
</style>
